import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Checkbox,
  Table,
  Typography,
  Form,
  Button,
  ConfigProvider,
  Input,
  Spin,
} from "antd";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import Row from "./components/Row";
import EditableCell from "./components/EditableCell";
import DragHandle from "./components/Draghandle";
import InnoIcons from "InnoUI/innoicons";
import SC from "./style";
import theme from "@theme";
import { Popover, TableList } from "@InnoUI";
import { Empty } from "antd";
import { Select } from "@InnoUI";
import { showAlert } from "utils/helper";
import debounce from "lodash.debounce";

const InnoTable = (props) => {
  const {
    data = [],
    column = [],
    tableConfig = [],
    expandable = null,
    loading = false,
    onRowClickCallback = () => {},
    onDragCallback = () => {},
    checkboxChangeCallback = () => {},
    allCheckboxChangeCallback = () => {},
    tableKey = "",
    searchKeys = [],
    generateDummyData = () => {},
    onSaveAddCallback = () => {},
    onAddCallback = () => {},
    onCancelCallback = () => {},
    onDeleteCallback = () => {},
    onAddDataCallback = () => {},
    backgroundColor,
    padding,
    color,
    sx,
    summary,
    scroll,
    onSearch = () => {},
    onFilterChange = () => {},
    popupState,
    ComponnentName,
    onRow = () => {},
    onDeleteCallbackModal = () => {},
    selectedItems = [],
    borderleft = "",
    tablepadding = "5px 20px 0px 25px",
    tableLayout,
  } = props;

  const [dataSource, setDataSource] = useState(data);
  const [editingKey, setEditingKey] = useState("");
  const [count, setCount] = useState(data.length);
  const [form] = Form.useForm();

  useEffect(() => {
    setDataSource(data);
    setCount(data.length);
  }, [data]);

  const isEditing = (record) => record.id === editingKey;

  const edit = (record) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.id);
  };

  const saveRecord = async (id) => {
    try {
      const row = await form.validateFields();
      const newData = [...dataSource];
      const index = newData.findIndex((item) => id === item.id);
      if (index > -1) {
        const item = newData[index];
        const rowData = { ...item, ...row };
        newData.splice(index, 1, rowData);
        setDataSource(newData);
        onSaveAddCallback(rowData);
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const handleDelete = (id) => {
    showAlert({
      title: "Are you sure you want to delete?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        onDeleteCallback(id);
      }
    });
  };

  const handleDeleteOpenModal = (id) => {
    onDeleteCallbackModal(id);
  };

  const handleCancel = (id) => {
    const newData = dataSource.filter((item) => item.id !== id);
    setDataSource(newData);
    onCancelCallback(id);
    setCount(count - 1);
  };

  const handleAdd = () => {
    const maxId =
      data.length > 0
        ? data.reduce(
            (max, obj) => (parseInt(obj.id) > max ? parseInt(obj.id) : max),
            data[0].id,
          )
        : 0;
    const newData = generateDummyData(maxId);
    setDataSource([newData, ...dataSource]);
    setCount(count + 1);
    edit(newData);
    onAddCallback(newData);
  };

  const handleAddData = () => {
    onAddDataCallback();
  };

  const onDragEnd = useCallback(
    ({ active, over }) => {
      if (active.id !== over?.id) {
        setDataSource((prevState) => {
          const activeIndex = prevState.findIndex(
            (record) => record.id === active?.id,
          );
          const overIndex = prevState.findIndex(
            (record) => record.id === over?.id,
          );

          const newData = arrayMove(prevState, activeIndex, overIndex);
          onDragCallback(newData);
          return newData;
        });
      }
    },
    [onDragCallback],
  );

  /* const handleCheckboxChange = useCallback(
    (id, checked) => {
      checkboxChangeCallback(id, checked);
    },
    [dataSource, checkboxChangeCallback]
  ); */

  const handleCheckboxChange = useCallback(
    (id, checked) => {
      const newData = dataSource.map((item) =>
        item.id === id ? { ...item, selected: checked } : item,
      );
      setDataSource(newData);
      checkboxChangeCallback(id, checked);
    },
    [dataSource, checkboxChangeCallback],
  );

  const handleAllCheckboxChange = useCallback(
    (checked) => {
      const allIds = []; // To store all selected ids

      const newData = dataSource.map((item) => {
        if (checked) {
          allIds.push(item.id); // Push the id to allIds if checked
        }
        return {
          ...item,
          selected: checked, // Set the selected field to the checked value
        };
      });

      setDataSource(newData);
      allCheckboxChangeCallback(allIds, checked); // Pass the checked state and all selected ids
    },
    [dataSource, allCheckboxChangeCallback],
  );

  const handleRowClick = useCallback(
    (record) => {
      onRowClickCallback(record);
    },
    [onRowClickCallback],
  );

  const handleKeyDown = (event, id, action) => {
    if (event.key === "Enter") {
      switch (action) {
        case "save":
          saveRecord(id);
          break;
        case "cancel":
          handleCancel(id);
          break;
        case "delete":
          handleDelete(id);
          break;
      }
    }
  };

  // const dragDef = {
  //   render(_, record) {
  //     return {
  //       props: {
  //         style: { width: "20px" },
  //       },
  //       children: <DragHandle />,
  //     };
  //   },
  // };

  const dragDef = {
    width: 20,
    onCell: () => ({
      style: { width: "20px" }, // Adjust cell style
    }),
    render: () => <DragHandle />,
  };

  const selectDef = {
    title: (
      <Checkbox
        checked={
          dataSource.length > 0 &&
          selectedItems.length > 0 &&
          dataSource.every((item) => selectedItems.includes(item.id))
        }
        onChange={(e) => {
          e.stopPropagation();
          handleAllCheckboxChange(e.target.checked);
        }}
      />
    ),
    onCell: (record) => ({
      style: { width: "20px" },
    }),
    render: (_, record) => (
      <Checkbox
        checked={selectedItems.includes(record.id)}
        onChange={(e) => {
          e.stopPropagation();
          handleCheckboxChange(record.id, e.target.checked);
        }}
      />
    ),
  };

  const inlineEditDef = {
    render: (text, record) => (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>{text}</span>
        <span>
          <InnoIcons.EditOutlined
            style={{ marginLeft: 8 }}
            onClick={() => edit(record)}
          />
          <InnoIcons.ShrinkOutlined
            style={{ marginLeft: 20 }}
            onClick={() => handleRowClick(record)}
          />
        </span>
      </div>
    ),
  };

  const operationPref = {
    title: "Action",
    width: 100,
    align: "center",
    dataIndex: "operation",
    onCell: () => ({
      style: {
        textAlign: "center",
      },
    }),
    render: (_, record) => {
      const commonStyles = {
        fontSize: "14px",
        cursor: "pointer",
        color: theme.colors.themeColor,
      };
      const saveIcon = (
        <Typography.Link
          onClick={() => saveRecord(record.id)}
          style={{ marginRight: 7 }}
          tabIndex="0"
          onKeyDown={(event) => handleKeyDown(event, record.id, "save")}
        >
          <InnoIcons.SaveIcon style={{ marginRight: 8, ...commonStyles }} />
        </Typography.Link>
      );

      const cancelIcon = (
        <Typography.Link
          onClick={() => handleCancel(record.id)}
          tabIndex="0"
          onKeyDown={(event) => handleKeyDown(event, record.id, "cancel")}
        >
          <InnoIcons.CancelIcon style={{ marginRight: 30, ...commonStyles }} />
        </Typography.Link>
      );

      const deleteIcon = (
        // <InnoIcons.DeleteIcon
        //   onClick={() => handleDelete(record.id)}
        //   style={{
        //     color: theme.colors.themeColor,
        //     ...commonStyles,
        //   }}
        //   tabIndex="0"
        //   onKeyDown={(event) => handleKeyDown(event, record.id, "delete")}
        // />
        <InnoIcons.DeleteIcon
          onClick={() => handleDeleteOpenModal(record.id)}
          style={{
            color: theme.colors.themeColor,
            ...commonStyles,
          }}
          tabIndex="0"
          onKeyDown={(event) => handleKeyDown(event, record.id, "delete")}
        />
      );

      return record.isLocal ? (
        <span>
          {saveIcon}
          {cancelIcon}
        </span>
      ) : (
        deleteIcon
      );
    },
  };

  /* const seralNoPref = {
    title: "S. No.",
    width: 80,
    dataIndex: "id",
    onCell: () => ({}),
    render: (value, record, index) => (
      <span>{dataSource.findIndex((item) => item?.id === record?.id) + 1}</span>
    ),
  }; */

  const seralNoPref = {
    title: "S. No.",
    width: 80,
    fixed: "left",
    render: (_, __, index) =>
      (tableConfig.pagination.current - 1) * tableConfig.pagination.pageSize +
      index +
      1,
  };

  const createColumnDef = useCallback(
    (data) => {
      let columns = [];

      data.forEach((column) => {
        let obj = {
          ...column,
        };

        if (column.inlineEdit) {
          obj = { ...obj, ...inlineEditDef };
        }

        columns.push(obj);
      });

      if (tableConfig.sno) {
        columns = [seralNoPref, ...columns];
      }

      if (tableConfig.draggable) {
        columns = [dragDef, ...columns];
      }
      if (tableConfig.selection) {
        columns = [selectDef, ...columns];
      }
      if (tableConfig.action) {
        columns.push(operationPref);
      }

      return columns;
    },
    [
      handleAllCheckboxChange,
      handleCheckboxChange,
      handleRowClick,
      isEditing,
      tableConfig,
    ],
  );

  const mergedColumns = createColumnDef(column).map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        render: col.render,
        type: col.type,
      }),
    };
  });

  const debouncedOnSearch = useRef(
    debounce((term) => {
      onSearch(term);
    }, 500),
  ).current;

  const searchData = (inputValue) => {
    try {
      const normalizedInputValue = inputValue.trimStart().toLowerCase(); // Only trims leading spaces
      // onSearch(inputValue);
      debouncedOnSearch(inputValue);
      if (normalizedInputValue) {
        const filteredData = data.filter((item) =>
          searchKeys.some((key) => {
            const keysArray = key.split(".");
            let value = item;

            for (let k of keysArray) {
              if (value === undefined || value === null) {
                return false; // Skip undefined or null values at any level
              }

              if (Array.isArray(value)) {
                // If the value is an array, check if any object in the array has the key
                return value.some((subItem) => {
                  let subValue = subItem;
                  const subKeysArray = keysArray.slice(keysArray.indexOf(k));
                  for (let subK of subKeysArray) {
                    subValue = subValue ? subValue[subK] : undefined;
                    if (subValue === undefined || subValue === null) {
                      return false;
                    }
                  }
                  return subValue
                    ? subValue
                        .toString()
                        .trimStart()
                        .toLowerCase()
                        .includes(normalizedInputValue)
                    : false;
                });
              } else {
                // If the value is not an array, navigate normally
                value = value[k];
                if (value === undefined || value === null) {
                  return false;
                }
              }
            }

            return value
              ? value.toString().toLowerCase().includes(normalizedInputValue)
              : false;
          }),
        );
        setDataSource(filteredData);
      } else {
        setDataSource(data);
      }
    } catch (e) {
      console.log(e);
      setDataSource(data);
    }
  };

  const onApplyingFilter = (pagination, filters, sorter, extra) => {
    // forward pagination, filters and sorter to parent
    // signature: onFilterChange(filters, sorter, pagination)
    onFilterChange(filters, sorter, pagination);
  };

  const TableLoader = ({ size = "medium" }) => (
    <ConfigProvider
      theme={{
        token: {
          // Seed Token
          colorPrimary: "orange",
          // Alias Token
        },
      }}
    >
      <Spin size={size} />
    </ConfigProvider>
  );

  return (
    <>
      {tableConfig.divBorder && (
        <div
          style={{
            display: "flex",
            justifyContent: "",
            flex: 2,
            borderBottom: `1px solid  ${theme.colors.bordertask}`,
            padding: "10px 20px 0px 20px",
            marginBottom: "20px",
          }}
        >
          {ComponnentName && <SC.HeadingTag>{ComponnentName}</SC.HeadingTag>}

          {tableConfig.search && (
            <div style={{ display: "flex", flex: 1 }}>
              <form
                autoComplete="off"
                onSubmit={(event) => {
                  event.preventDefault();
                }}
              >
                <SC.InputContiner>
                  <SC.SearchIcon />
                  <SC.InputStyle
                    placeholder={tableConfig?.searchPlaceholder || "Search"}
                    onChange={(e) => searchData(e.target.value)}
                  />
                </SC.InputContiner>
              </form>
              <ConfigProvider
                theme={{
                  token: {
                    // Seed Token
                    colorPrimary: theme.colors.themeColor,
                    borderRadius: 2,
                    // Alias Token
                  },
                }}
              >
                {/* <Input.Search
                style={{ margin: "0 0 10px 0", width: "300px" }}
                placeholder={tableConfig?.searchPlaceholder || "Search..."}
                enterButton
                onSearch={(value) => searchData(value)}
                //onChange={(e) => searchData(e.target.value)}
              /> */}
                {tableConfig.menuItems && (
                  <Popover
                    sx={{
                      cursor: "pointer",
                      color: theme.colors.themeColor,
                    }}
                    buttonLabel={{
                      icon: <InnoIcons.MoreVertIcon fontSize={"large"} />,
                    }}
                    popoverContent={
                      <TableList
                        sx={{
                          margin: 0,
                          borderBottom: `1px solid ${theme.colors.bordertask}`,
                          padding: "5px 10px 5px 10px",
                        }}
                        items={tableConfig?.menuItems || []}
                        popupState={popupState}
                      />
                    }
                  />
                )}
              </ConfigProvider>
            </div>
          )}
          {tableConfig.addNew && (
            <Button
              onClick={handleAddData}
              style={{
                marginBottom: 8,
                background: theme.gradient.themeGradient,
                color: theme.colors.white,
                minWidth: "120px",
                borderRadius: "4px",
                fontSize: "12px",
              }}
              icon={
                <InnoIcons.PlusOutlined
                  style={{ color: theme.colors.default }}
                />
              }
            >
              New {tableKey}
            </Button>
          )}
          {tableConfig.addDummyData && (
            <Button
              onClick={handleAdd}
              style={{
                marginBottom: 8,
                background: theme.gradient.themeGradient,
                color: theme.colors.white,
                minWidth: "120px",
                borderRadius: "4px",
                fontSize: "12px",
              }}
              icon={
                <InnoIcons.PlusOutlined
                  style={{ color: theme.colors.default }}
                />
              }
            >
              New {tableKey}
            </Button>
          )}

          {tableConfig.bulkAction && tableConfig.bulkAction.allowed && (
            <div style={{ width: "150px", marginLeft: "5px" }}>
              <Select.BasicSelect
                //value={value}
                onChange={(data) => tableConfig.bulkAction.onChange(data)}
                options={tableConfig?.bulkAction?.bulkOptions || []}
                label={tableConfig?.bulkAction?.title}
                sx={{ fontSize: "13px" }}
              />
            </div>
          )}
        </div>
      )}
      <div style={{ padding: tablepadding }}>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={dataSource && dataSource.map((i) => i?.id)}
            strategy={verticalListSortingStrategy}
          >
            <SC.TableContainer
              backgroundColor={backgroundColor}
              padding={padding}
              color={color}
              sx={sx}
              borderleft={borderleft}
            >
              <ConfigProvider
                theme={{
                  token: {
                    colorPrimary: theme.colors.themeColor,
                    borderRadius: 2,
                  },
                }}
              >
                <Form form={form} component={false}>
                  <Table
                    rowClassName={() => "row-class"}
                    expandable={
                      expandable
                        ? {
                            ...expandable,
                            expandIcon: ({ expanded, onExpand, record }) =>
                              expanded ? (
                                <InnoIcons.DownOutlined
                                  onClick={(e) => onExpand(record, e)}
                                />
                              ) : (
                                <InnoIcons.RightOutlined
                                  onClick={(e) => onExpand(record, e)}
                                />
                              ),
                          }
                        : undefined
                    }
                    components={{
                      body: {
                        row: Row,
                        cell: EditableCell,
                      },
                    }}
                    bordered
                    columns={mergedColumns}
                    dataSource={dataSource}
                    loading={{
                      indicator: <TableLoader />,
                      spinning: loading,
                    }}
                    pagination={tableConfig.pagination}
                    rowKey={"id"}
                    summary={summary}
                    scroll={scroll}
                    onChange={onApplyingFilter}
                    tableLayout={tableLayout}
                    onRow={onRow}
                    locale={{
                      emptyText: (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          styles={{
                            height: 60,
                          }}
                          description={
                            <Typography.Text>
                              {loading ? "Loading..." : "No data"}
                            </Typography.Text>
                          }
                        ></Empty>
                      ),
                    }}
                  />
                </Form>
              </ConfigProvider>
            </SC.TableContainer>
          </SortableContext>
        </DndContext>
      </div>
    </>
  );
};

export default InnoTable;
